import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, exhaustMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';

import * as CampaignActions from './campaign.actions';
import { CampaignService } from '../services/campaign.service';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { ToastService } from 'app/toast/services/toast.service';
import { PresetOptionsService } from '../services/preset-options.service';
import * as DialogActions from 'app/dialog/features/dialog.actions';
import { AuctionManageService } from 'app/manage-auctions/services/auction-manage/auction-manage.service';


@Injectable()
export class CampaignEffects {

  loadCampaignList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadCampaignList),
      exhaustMap((action: any) => {
        const statusFilter = Object.hasOwnProperty.call(action, "statusFilter") ? action.statusFilter : this.campaignService.statusFilter;
        const campaignTypes = action.campaignTypes || this.campaignService.campaignTypes;
        return this.campaignService.getCampaignList(action.payload, {statusFilter, campaignTypes}).pipe(
          map(data => {
            return CampaignActions.loadCampaignListSuccess({ data })
          }),
          catchError(error => of(CampaignActions.loadCampaignListFailure({ error }))))
      })
    );
  });

  loadEditCampaignList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadEditCampaignList),
      exhaustMap((action: any) => {
        const statusFilter = Object.hasOwnProperty.call(action, "statusFilter") ? action.statusFilter : this.campaignService.statusFilter;
        const campaignTypes = action.campaignTypes || this.campaignService.campaignTypes;
        return this.campaignService.getEditCampaignList(action.payload, {statusFilter, campaignTypes}).pipe(
          map(data => {
            return CampaignActions.loadEditCampaignListSuccess({ data })
          }),
          catchError(error => of(CampaignActions.loadEditCampaignListFailure({ error }))))
      })
    );
  });

  loadEventCampaignList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadEventCampaignList),
      exhaustMap((action: any) => {
        const statusFilter = Object.hasOwnProperty.call(action, "statusFilter") ? action.statusFilter : this.campaignService.statusFilter;
        const campaignTypes = action.campaignTypes || this.campaignService.campaignTypes;
        return this.campaignService.getEventCampaignList(action.payload, {statusFilter, campaignTypes}).pipe(
          map(data => {
            return CampaignActions.loadEventCampaignListSuccess({ data })
          }),
          catchError(error => of(CampaignActions.loadEventCampaignListFailure({ error }))))
      })
    );
  });

  loadManageCampaignList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadManageCampaignList),
      exhaustMap((action: any) => {
        const statusFilter = Object.hasOwnProperty.call(action, "statusFilter") ? action.statusFilter : this.campaignService.statusFilter;
        const campaignTypes = action.campaignTypes || this.campaignService.campaignTypes;
        return this.campaignService.getManageCampaignList(action.payload, {statusFilter, campaignTypes}).pipe(
          map(data => {
            return CampaignActions.loadManageCampaignListSuccess({ data })
          }),
          catchError(error => of(CampaignActions.loadManageCampaignListFailure({ error }))))
      })
    );
  });

  loadReportCampaignList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadReportCampaignList),
      exhaustMap((action: any) => {
        const statusFilter = Object.hasOwnProperty.call(action, "statusFilter") ? action.statusFilter : this.campaignService.statusFilter;
        const campaignTypes = action.campaignTypes || this.campaignService.campaignTypes;
        return this.campaignService.getReportCampaignList(action.payload, {statusFilter, campaignTypes}).pipe(
          map(data => {
            return CampaignActions.loadReportCampaignListSuccess({ data })
          }),
          catchError(error => of(CampaignActions.loadReportCampaignListFailure({ error }))))
      })
    );
  });

  loadPendingCampaignList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadPendingCampaignList),
      exhaustMap((action: any) => {
        const statusFilter = Object.hasOwnProperty.call(action, "statusFilter") ? action.statusFilter : this.campaignService.statusFilter;
        const campaignTypes = action.campaignTypes || this.campaignService.campaignTypes;
        return this.campaignService.getPendingCampaignList(action.payload, {statusFilter, campaignTypes}).pipe(
          map(data => {
            return CampaignActions.loadPendingCampaignListSuccess({ data })
          }),
          catchError(error => of(CampaignActions.loadPendingCampaignListFailure({ error }))))
      })
    );
  });

  loadCampaign$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadCampaign),
      exhaustMap((action: any) => {
        const campaignId = action.payload.id;
        return this.campaignService.getCampaign(campaignId).pipe(
          map(data => {
            return CampaignActions.loadCampaignSuccess({ data })
          }),
          catchError(error => of(CampaignActions.loadCampaignFailure({ error }))))
      })
    );
  });

  loadHinduDateLocations$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.loadHinduDateLocations),
      exhaustMap((action: any) => {
        return this.campaignService.getHinduDateLocations().pipe(
          map(data => CampaignActions.loadHinduDateLocationsSuccess({ data })),
          catchError(error => of(CampaignActions.loadHinduDateLocationsFailure({ error }))))
      })
    );
  });

  createCampaign$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.createCampaign),
      exhaustMap(({ payload, redirect }: any) => {
        return this.campaignService.createCampaign(payload).pipe(
          map(data => CampaignActions.createCampaignSuccess({ payload, redirect, data })),
          catchError(error => of(CampaignActions.createCampaignFailure({ error }))))
      })
    );
  });

  createCampaignSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.createCampaignSuccess),
      concatMap(({ payload, redirect, data }) => {
        if(redirect) {
          this.router.navigateByUrl('campaign/list');
          return of({
            type: CampaignActions.createCampaignSuccessEmpty.type
          });
        }
        return of({
          type: CampaignActions.selectCampaign.type,
          payload: data
        })
      })
    );
  });

  putCampaign$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.putCampaign),
      exhaustMap(action => {
        const { payload, redirect } = action;
        const update = _.get(action, 'update', true);
        return this.campaignService.updateCampaign(payload).pipe(
          map(data => CampaignActions.putCampaignSuccess({ payload, redirect, update, data })),
          catchError(error => of(CampaignActions.putCampaignFailure({ error }))))
      })
    );
  });

  putCampaignSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.putCampaignSuccess),
      concatMap((action) => {
        const { redirect, update, data } = action;

        if(redirect) {
          this.router.navigateByUrl('campaign/list');
          return of({
            type: CampaignActions.putCampaignSuccessEmpty.type
          });
        }

        if(update) {
          return of({
            type: CampaignActions.selectCampaign.type,
            payload: data
          })
        }

        return of({
          type: CampaignActions.putCampaignSuccessEmpty.type
        });
      })
    );
  });


  putCampaignByProperties$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CampaignActions.putCampaignByProperties),
      exhaustMap(({ propertiesToUpdate, campaignId }) => {
        return this.campaignService.updateCampaignByProperties(campaignId, propertiesToUpdate).pipe(
          map(data => {
            return CampaignActions.putCampaignByPropertiesSuccess({
              updatedCampaign: data
            })
          }),
          catchError(error => of(CampaignActions.putCampaignByPropertiesFailure(error)))
        )
      })
    )
  })

  deleteCampaign$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.deleteCamapign),
      exhaustMap(({ payload }) => {
        return this.campaignService.deleteCampaign(payload).pipe(
          map(data => CampaignActions.deleteCamapignSuccess({ payload, data })),
          catchError(error => of(CampaignActions.deleteCamapignFailure({ error }))))
      })
    );
  });

  deleteCampaignSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.deleteCamapignSuccess),
      concatMap(({ payload: id }) => {
        const updateCampaignStatusAction = CampaignActions.updateCampaignStatus({
          payload: { id, status: 2 }
        });
        return of(updateCampaignStatusAction);
      })
    );
  });

  activateCampaign$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.activateCamapign),
      exhaustMap(({ payload }) => {
        return this.campaignService.activateCampaign(payload).pipe(
          map(data => CampaignActions.activateCamapignSuccess({ payload, data })),
          catchError(error => of(CampaignActions.activateCamapignFailure({ error }))))
      })
    );
  });

  activateCampaignSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.activateCamapignSuccess),
      concatMap(() => {
        const loadCampaign = CampaignActions.loadCampaignList({
          payload: this.sharedService.token
        });
        return of(loadCampaign);
      })
    );
  });

  setStatus$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.setStatusCamapign),
      exhaustMap(({ payload: { campaignId, newStatus } }) => {
        return this.campaignService.setStatus(campaignId, newStatus).pipe(
          map(data => CampaignActions.setStatusCamapignSuccess({ payload: { campaignId, newStatus }, data })),
          catchError(error => of(CampaignActions.setStatusCamapignFailure({ error }))))
      })
    );
  });

  setStatusSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.setStatusCamapignSuccess),
      concatMap(() => {
        const loadCampaign = CampaignActions.loadCampaignList({
          payload: this.sharedService.token
        });
        return of(loadCampaign);
      })
    );
  });

  resetCampaign$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.resetCamapign),
      exhaustMap(({ payload }) => {
        return this.campaignService.resetCampaign(payload).pipe(
          tap(()=> {
            return this.toastService.show('success', 'Campaign reset is successful.', {delay: 1000});
          }),
          map(data => CampaignActions.resetCamapignSuccess({ payload, data })),
          catchError(error => of(CampaignActions.resetCamapignFailure({ error }))))
      })
    );
  });

  raffleDraw$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.raffleDraw),
      exhaustMap(({ payload }) => {
        return this.campaignService.raffleDraw(payload).pipe(
          map(data => CampaignActions.raffleDrawSuccess({ payload, data })),
          catchError(error => of(CampaignActions.raffleDrawFailure({ error }))))
      })
    );
  });

  raffleDrawSuccess$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.raffleDrawSuccess),
      concatMap(({ data }) => {
        let isArray = data instanceof Array
        let initialState = null

        if(isArray){
          initialState = {
            winers:data,
            isArray,
            callback: () => {}
          }
        }else{
          initialState ={
            name: data.name,
            email: data.email,
            isArray,
            callback: () => {}
          } 
        }

        const action = DialogActions.openDialog({
          payload: {
            name: "RaffleDraw",
            options: {
              size: 'small',
              modalClass:`modal--paybee ${isArray ? 'modal-lg': ''}`,
              containerClass:'mdb-modal-backdrop-paybee',

            },
            initialState
          }
        });
        return of(action);
      })
    );
  });

  addCampaignPresetOption$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.addCampaignPresetOption),
      exhaustMap(({ payload: { campaign, option } }) => {
        return this.presetOptionsService.addPresetOption(campaign, option).pipe(
          map(data => CampaignActions.addCampaignPresetOptionSuccess({ campaign, option, data })),
          catchError(error => of(CampaignActions.addCampaignPresetOptionFailure({ error }))))
      })
    );
  });

  updateCampaignPresetOption$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.updateCampaignPresetOption),
      exhaustMap(({ payload: { campaign, option } }) => {
        return this.presetOptionsService.updatePresetOption(campaign, option).pipe(
          map(data => CampaignActions.updateCampaignPresetOptionSuccess({ campaign, option, data })),
          catchError(error => of(CampaignActions.updateCampaignPresetOptionFailure({ error }))))
      })
    );
  });

  deleteCampaignPresetOption$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.deleteCampaignPresetOption),
      exhaustMap(({ payload: { campaign, option } }) => {
        return this.presetOptionsService.deletePresetOption(campaign, option).pipe(
          map(data => CampaignActions.deleteCampaignPresetOptionSuccess({ campaign, option, data })),
          catchError(error => of(CampaignActions.deleteCampaignPresetOptionFailure({ error }))))
      })
    );
  });

  newCreateCampaignPresetOption$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.newCreateCampaignPresetOption),
      exhaustMap(({ payload: { campaign, option } }) => {
        return this.presetOptionsService.addPresetOption(campaign, option).pipe(
          map(data => CampaignActions.newCreateCampaignPresetOptionSuccess({ campaign, option, data })),
          catchError(error => of(CampaignActions.addCampaignPresetOptionFailure({ error }))));
      })
    );
  });

  newUpdateCampaignPresetOption$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.newUpdateCampaignPresetOption),
      exhaustMap(({ payload: { campaign, option } }) => {
        return this.presetOptionsService.updatePresetOption(campaign, option).pipe(
          map(data => CampaignActions.newUpdateCampaignPresetOptionSuccess({ campaign, option, data })),
          catchError(error => of(CampaignActions.updateCampaignPresetOptionFailure({ error }))));
      })
    );
  });

  newDeleteCampaignPresetOption$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.newDeleteCampaignPresetOption),
      exhaustMap(({ payload: { campaign, option } }) => {
        return this.presetOptionsService.deletePresetOption(campaign, option).pipe(
          tap(() => {
            this.toastService.show('success', 'Deleted Successfully');
          }),
          map(data => CampaignActions.newDeleteCampaignPresetOptionSuccess({ campaign, option, data })),
          catchError(error => of(CampaignActions.deleteCampaignPresetOptionFailure({ error }))))
      })
    );
  });

  restoreCampaignPresetOption$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(CampaignActions.restoreCampaignPresetOption),
      exhaustMap(({ payload: { campaign, option } }) => {
        return this.presetOptionsService.restorePresetOption(campaign, option).pipe(
          tap(() => {
            this.toastService.show('success', 'Restored Successfully');
          }),
          map(data => CampaignActions.restoreCampaignPresetOptionSuccess({ option })),
          catchError(error => of(CampaignActions.deleteCampaignPresetOptionFailure({ error }))))
      })
    );
  });


  constructor(
    private actions$: Actions,
    private campaignService: CampaignService,
    private presetOptionsService: PresetOptionsService,
    private toastService: ToastService,
    private sharedService: SharedService,
    private auctionManageService:AuctionManageService,
    private router: Router
  ) {}

}
